// exportTable.js
// Shared print / Excel / PDF exporters.
// All three operate on the currently visible (filtered) rows.

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Trigger a browser print dialog with a clean printable table.
 * @param {string} title      Document title (used as heading)
 * @param {string[]} columns  Column headers
 * @param {Array<Array<string|number>>} data  Row data (array of arrays)
 */
export function exportToPrint(title, columns, data) {
    const w = window.open('', '_blank', 'width=1000,height=700');
    if (!w) {
        alert('Please allow pop-ups to use Print.');
        return;
    }

    const styleRow = `
        <style>
            * { font-family: Inter, -apple-system, "Segoe UI", sans-serif; }
            body { padding: 28px; color: #263247; }
            h1 { font-size: 20px; margin: 0 0 4px; font-weight: 800; }
            .meta { color: #8793a5; font-size: 12px; margin-bottom: 18px; }
            table { width: 100%; border-collapse: collapse; }
            th, td {
                padding: 9px 11px;
                border-bottom: 1px solid #e4e9f1;
                font-size: 11.5px;
                text-align: left;
                vertical-align: top;
                word-break: break-word;
            }
            th {
                background: #f7f8fb;
                text-transform: uppercase;
                font-size: 10px;
                letter-spacing: .05em;
                color: #667388;
                font-weight: 800;
                border-bottom: 1px solid #d8dfeb;
            }
            tr:nth-child(even) td { background: #fbfcff; }
            @media print {
                body { padding: 0; }
            }
        </style>
    `;

    const headerCells = columns.map(c => `<th>${escapeHtml(c)}</th>`).join('');
    const bodyRows = data.map(row =>
        `<tr>${row.map(cell => `<td>${escapeHtml(cell ?? '')}</td>`).join('')}</tr>`
    ).join('');

    const now = new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

    w.document.write(`
        <!DOCTYPE html>
        <html><head><title>${escapeHtml(title)}</title>${styleRow}</head>
        <body>
            <h1>${escapeHtml(title)}</h1>
            <div class="meta">Generated ${now} &nbsp;•&nbsp; ${data.length} record${data.length !== 1 ? 's' : ''}</div>
            <table>
                <thead><tr>${headerCells}</tr></thead>
                <tbody>${bodyRows}</tbody>
            </table>
        </body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 250);
}

/**
 * Download an Excel (.xlsx) file.
 */
export function exportToExcel(title, columns, data) {
    const wsData = [
        columns,
        ...data
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto width
    ws['!cols'] = columns.map((c, i) => {
        const maxLen = Math.max(
            String(c).length,
            ...data.map(row => String(row[i] ?? '').length)
        );
        return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    const safeName = title.replace(/[^\w\-]+/g, '_');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `${safeName}_${stamp}.xlsx`);
}

/**
 * Download a PDF via jsPDF + autotable.
 */
export function exportToPDF(title, columns, data) {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(title, 40, 40);

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(120);
    const now = new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
    doc.text(`Generated ${now} • ${data.length} record${data.length !== 1 ? 's' : ''}`, 40, 58);

    autoTable(doc, {
        head: [columns],
        body: data,
        startY: 76,
        styles: {
            fontSize: 9,
            cellPadding: 6,
            overflow: 'linebreak',
        },
        headStyles: {
            fillColor: [77, 70, 201],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 9,
        },
        alternateRowStyles: {
            fillColor: [248, 250, 253],
        },
        margin: { left: 40, right: 40, top: 60, bottom: 40 },
    });

    const safeName = title.replace(/[^\w\-]+/g, '_');
    const stamp = new Date().toISOString().slice(0, 10);
    doc.save(`${safeName}_${stamp}.pdf`);
}

// ---- tiny helper ----
function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}